///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2025, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2025 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { Box3, MathUtils, Sphere, Vector2, Vector3 } from "three";
import type { Viewer } from "../Viewer";

export function zoomTo(viewer: Viewer, box: Box3): void {
  if (box.isEmpty()) return;

  const boxCenter = box.getCenter(new Vector3());
  const boxSize = box.getBoundingSphere(new Sphere()).radius;

  const rendererSize = viewer.renderer.getSize(new Vector2());
  const aspect = rendererSize.x / rendererSize.y;

  const camera = viewer.camera as any;

  if (camera.isPerspectiveCamera) {
    const offset = new Vector3(0, 0, 1)
      .applyQuaternion(camera.quaternion)
      .multiplyScalar(boxSize / Math.tan(MathUtils.DEG2RAD * camera.fov * 0.5));

    camera.position.copy(offset).add(boxCenter);
    camera.updateMatrixWorld();
  }
  if (camera.isOrthographicCamera) {
    camera.top = boxSize;
    camera.bottom = -boxSize;
    camera.left = camera.bottom * aspect;
    camera.right = camera.top * aspect;
    camera.zoom = 1;
    camera.updateProjectionMatrix();

    const offset = new Vector3(0, 0, 1)
      .applyQuaternion(camera.quaternion)
      .multiplyScalar(viewer.extents.getBoundingSphere(new Sphere()).radius * 3);

    camera.position.copy(offset).add(boxCenter);
    camera.updateMatrixWorld();
  }

  viewer.target.copy(boxCenter);

  viewer.update();
  viewer.emitEvent({ type: "zoom" });
}
